import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../utils/api';

interface Purpose {
  id: number;
  name: string;
  is_active: boolean;
}

const PurposeManagement: React.FC = () => {
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentPurpose, setCurrentPurpose] = useState<Partial<Purpose>>({});
  const [error, setError] = useState<string | null>(null);

  // Fetch purposes
  const fetchPurposes = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/purposes');
      setPurposes(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch purposes');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPurposes();
  }, []);

  // Open add/edit dialog
  const handleOpenDialog = (mode: 'add' | 'edit', purpose?: Purpose) => {
    setDialogMode(mode);
    setCurrentPurpose(mode === 'edit' && purpose ? { ...purpose } : { name: '' });
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPurpose({});
    setError(null);
  };

  // Add or edit purpose
  const handleSavePurpose = async () => {
    if (!currentPurpose.name || !currentPurpose.name.trim()) {
      setError('Purpose name is required');
      return;
    }
    setError(null);
    try {
      if (dialogMode === 'add') {
        await api.post('/api/purposes', { name: currentPurpose.name });
      } else if (dialogMode === 'edit' && currentPurpose.id) {
        await api.put(`/api/purposes/${currentPurpose.id}`, { name: currentPurpose.name });
      }
      fetchPurposes();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save purpose');
    }
  };

  // Delete purpose
  const handleDeletePurpose = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this purpose?')) return;
    try {
      await api.delete(`/api/purposes/${id}`);
      fetchPurposes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete purpose');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Purpose Management
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog('add')}
        sx={{ mb: 2 }}
      >
        Add Purpose
      </Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purposes.map((purpose) => (
              <TableRow key={purpose.id}>
                <TableCell>{purpose.name}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="edit"
                    color="primary"
                    onClick={() => handleOpenDialog('edit', purpose)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    color="error"
                    onClick={() => handleDeletePurpose(purpose.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{dialogMode === 'add' ? 'Add Purpose' : 'Edit Purpose'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Purpose Name"
            fullWidth
            value={currentPurpose.name || ''}
            onChange={(e) => setCurrentPurpose({ ...currentPurpose, name: e.target.value })}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePurpose}>
            {dialogMode === 'add' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurposeManagement;
